/* eslint-disable */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { readRequestPayload } from "../error-reporting/RequestContext";

export class ApiError extends Error {
  __proto__: ApiError;

  data: any;

  status: number;

  // The request path is needed by error logging: it shows which call failed.
  url?: string;

  // The payload is needed by error logging too: a rejected lookup is only actionable once the
  // log says what was looked up. It is summarised and masked before it leaves the browser.
  requestData?: any;

  constructor(data: any, status = 500, statusText = 'Internal Server Error', url?: string, requestData?: any) {
    super(`${status} ${statusText}`);

    this.constructor = ApiError;
    this.__proto__ = ApiError.prototype;

    this.name = this.constructor.name;
    this.data = data;
    this.status = status;
    this.url = url;
    this.requestData = requestData;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  inspect() {
    return this.stack;
  }
}

/**
 * The booking service could not answer: no HTTP response at all (network down, CORS, the 30 s
 * client timeout — axios then puts a string code such as 'ERR_NETWORK' into `status`), or a
 * gateway status from the backend, which returns 504 when an upstream such as Mulesoft is slow.
 * Pages treat these as "try again later", never as a business outcome like "vehicle not found".
 */
export function isServiceUnavailable(error: { status?: unknown } | null | undefined): boolean {
  if (!error) return false;

  return typeof error.status === 'string' || error.status === 502 || error.status === 503 || error.status === 504;
}

const throwApiError = ({ status, statusText, data, url, requestData }: any) => {
  console.error(`API Error: ${status} ${statusText}`, data);
  throw new ApiError(data, status, statusText, url, requestData);
};

export class ApiClient {
  _apiClient: AxiosInstance;

  _handlers: Map<string, any>;

  constructor(axiosConfig: AxiosRequestConfig) {
    this._handlers = new Map();

    this._apiClient = axios.create(axiosConfig);
    this._apiClient.interceptors.response.use(
      (response: AxiosResponse) => response.data,
      (error: any) => {
        if (axios.isCancel(error)) {
          throw error;
        }

        // Axios Network Error & Timeout error dont have 'response' field
        // https://github.com/axios/axios/issues/383
        const errorResponse = {
          ...(error.response || {
            status: error.code,
            statusText: error.message,
            data: error.data,
          }),
          url: error.config?.url,
          requestData: readRequestPayload(error.config),
        };

        const errorHandlers = this._handlers.get('error') || [];
        errorHandlers.forEach((handler: any) => {
          handler(errorResponse);
        });

        return throwApiError(errorResponse);
      },
    );
  }

  get<TResponse, TRequest = unknown>(
    url: string,
    params?: TRequest,
    requestConfig?: Omit<AxiosRequestConfig<TRequest>, 'params'>,
  ): Promise<TResponse> {
    return this._apiClient.get<TResponse, TResponse>(url, {
      ...requestConfig,
      params,
    });
  }

  post<TRequest = void, TResponse = unknown>(
    url: string,
    data?: TRequest,
    requestConfig?: Omit<AxiosRequestConfig<TRequest>, 'data'>,
  ): Promise<TResponse> {
    return this._apiClient.post<TResponse, TResponse>(url, data, requestConfig);
  }

  put<TRequest = void, TResponse = void>(
    url: string,
    data?: TRequest,
    requestConfig?: Omit<AxiosRequestConfig<TRequest>, 'data'>,
  ): Promise<TResponse> {
    return this._apiClient.put(url, data, requestConfig);
  }

  delete<TRequest = void, TResponse = void>(
    url: string,
    data?: TRequest,
    requestConfig?: Omit<AxiosRequestConfig<TRequest>, 'data'>,
  ): Promise<TResponse> {
    return this._apiClient.delete<TResponse, TResponse>(url, {
      ...requestConfig,
      data,
    });
  }

  on(event: string, handler: (...args: any[]) => void) {
    if (this._handlers.has(event)) {
      this._handlers.get(event).add(handler);
    } else {
      this._handlers.set(event, new Set([handler]));
    }

    return () => this._handlers.get(event).remove(handler);
  }
}
